import i18n from "i18next";
import { useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { Box, Paper, Typography } from "@mui/material";
import { VerticalBox } from "src/components/common/box/VerticalBox.tsx";
import { AavoFormFooter } from "src/components/common/forms/AavoFormFooter.tsx";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { AsyncSelectField } from "src/components/common/inputFields/AsyncSelectField.tsx";
import { AavoDatePicker } from "src/components/common/inputFields/AavoDatePicker.tsx";
import { AsyncRender } from "src/components/common/async/AsyncRender.tsx";
import { useAsyncState } from "src/utils/async/asyncState.ts";
import { TaskTemplateDataGridApi } from "src/api/generated/tasks/api/taskTemplateDataGridApi.ts";
import {
	TaskTemplateImportApi,
	TaskTemplateImportApi_TaskTemplateForImport,
} from "src/api/generated/tasks/api/taskTemplateImportApi.ts";
import { TaskTemplate } from "src/api/generated/postgres/db/types/tasks/tables/taskTemplate.ts";
import { TaskTemplateItem } from "src/api/generated/postgres/db/types/tasks/tables/taskTemplateItem.ts";
import { TaskSourceRef } from "src/api/generated/tasks/taskSource/taskSourceRef.ts";
import { UserApi } from "src/api/generated/users/api/userApi.ts";
import { AppUser } from "src/api/generated/postgres/db/types/role_management/tables/appUser.ts";
import { dayJsToDateIsoString, formatDayJs } from "src/utils/dayjsUtils.ts";

export interface ImportTasksFromTemplateDialogProps {
	taskSourceRef: TaskSourceRef;
	onCompleted: () => Promise<void>;
	closeDialog: () => Promise<void>;
}

export const ImportTasksFromTemplateDialog = ({
	taskSourceRef,
	onCompleted,
	closeDialog,
}: ImportTasksFromTemplateDialogProps) => {
	const [selectedTemplate, setSelectedTemplate] = useState<TaskTemplate | null>(null);
	const [referenceDate, setReferenceDate] = useState<Dayjs | null>(dayjs());
	const [templateForImportAsync, setTemplateForImportAsync, setTemplateForImportImmediate] =
		useAsyncState<TaskTemplateImportApi_TaskTemplateForImport | null>({ defaultValue: null });

	const [itemAssignedUserIds, setItemAssignedUserIds] = useState<Record<number, number | null>>({});

	const allUsersSelected =
		templateForImportAsync.data?.items.every((item) => itemAssignedUserIds[item.taskTemplateItemId] != null) ??
		false;

	const submitDisabledReason =
		selectedTemplate == null ? i18n.t("select_task_template")
		: referenceDate == null ? i18n.t("start_date")
		: templateForImportAsync.loading === true ? i18n.t("loading")
		: !allUsersSelected ? i18n.t("select_executor_for_all_tasks")
		: undefined;

	return (
		<VerticalBox sx={{ flex: 1, minHeight: 0 }}>
			<VerticalBox sx={{ flex: 1, padding: 2, gap: 2, overflowY: "auto" }}>
				<AsyncSelectField<TaskTemplate, number>
					label={i18n.t("task_template")}
					fetchOptions={async () => TaskTemplateDataGridApi.getTaskTemplates()}
					getOptionKey={(option) => option.taskTemplateId}
					getOptionLabel={(option) => option.name}
					onChange={onTemplateSelected}
				/>

				<AavoDatePicker
					label={i18n.t("start_date")}
					value={referenceDate}
					onChange={(date) => setReferenceDate(date)}
					disableClearable
				/>

				{selectedTemplate != null && (
					<AsyncRender
						asyncData={templateForImportAsync}
						content={(data) =>
							data == null ? null : (
								<TemplateItemsList
									items={data.items}
									referenceDate={referenceDate}
									itemAssignedUserIds={itemAssignedUserIds}
									onUserChange={(taskTemplateItemId, userId) => {
										setItemAssignedUserIds((prev) => ({
											...prev,
											[taskTemplateItemId]: userId,
										}));
									}}
								/>
							)
						}
					/>
				)}
			</VerticalBox>

			<AavoFormFooter>
				<AsyncButton label={i18n.t("cancel")} color={"secondary"} onClick={closeDialog} />
				<AsyncButton label={i18n.t("import")} disabled={submitDisabledReason} onClick={handleSubmit} />
			</AavoFormFooter>
		</VerticalBox>
	);

	async function onTemplateSelected(template: TaskTemplate | null) {
		setSelectedTemplate(template ?? null);
		setItemAssignedUserIds({});
		if (template == null) {
			setTemplateForImportImmediate(() => null);
			return;
		}
		await setTemplateForImportAsync(async () => {
			const data = await TaskTemplateImportApi.getTaskTemplateForImport({
				taskTemplateId: template.taskTemplateId,
			});
			setItemAssignedUserIds(
				Object.fromEntries(
					data.items.map((item: TaskTemplateItem) => [
						item.taskTemplateItemId,
						item.assignedToUserId ?? null,
					]),
				),
			);
			return data;
		});
	}

	async function handleSubmit() {
		const template = selectedTemplate;
		const date = referenceDate;
		const templateData = templateForImportAsync.data;
		if (template == null || date == null || templateData == null) return;

		const items = templateData.items.map((item: TaskTemplateItem) => ({
			taskTemplateItemId: item.taskTemplateItemId,
			assignedToUserId: itemAssignedUserIds[item.taskTemplateItemId]!,
		}));

		await TaskTemplateImportApi.importTasksFromTemplate({
			request: {
				sourceRef: taskSourceRef,
				referenceDate: dayJsToDateIsoString(date),
				items,
			},
		});

		await onCompleted();
		await closeDialog();
	}
};

interface TemplateItemsListProps {
	items: TaskTemplateItem[];
	referenceDate: Dayjs | null;
	itemAssignedUserIds: Record<number, number | null>;
	onUserChange: (taskTemplateItemId: number, userId: number | null) => void;
}

const TemplateItemsList = ({ items, referenceDate, itemAssignedUserIds, onUserChange }: TemplateItemsListProps) => {
	return (
		<VerticalBox sx={{ gap: 1, flex: 1 }}>
			<Typography variant={"subtitle2"}>{i18n.t("tasks")}</Typography>
			{items.map((item) => (
				<TemplateItemRow
					key={item.taskTemplateItemId}
					item={item}
					referenceDate={referenceDate}
					assignedToUserId={itemAssignedUserIds[item.taskTemplateItemId] ?? null}
					onUserChange={(userId) => onUserChange(item.taskTemplateItemId, userId)}
				/>
			))}
		</VerticalBox>
	);
};

interface TemplateItemRowProps {
	item: TaskTemplateItem;
	referenceDate: Dayjs | null;
	assignedToUserId: number | null;
	onUserChange: (userId: number | null) => void;
}

const TemplateItemRow = ({ item, referenceDate, assignedToUserId, onUserChange }: TemplateItemRowProps) => {
	const deadlineDate = referenceDate?.add(item.daysToDeadlineDate, "day") ?? null;
	const earliestStartDate =
		item.daysToEarliestStartDate != null ? (referenceDate?.add(item.daysToEarliestStartDate, "day") ?? null) : null;

	return (
		<Paper
			variant={"outlined"}
			sx={{
				alignSelf: "stretch",
				display: "grid",
				gridTemplateColumns: "1fr 1fr",
				gap: 1,
				alignItems: "center",
				padding: 1.5,
			}}
		>
			<Box>
				<Typography variant={"body1"}>{item.title}</Typography>
				<Typography variant={"body2"} color={"text.secondary"}>
					{i18n.t("deadline_date")}: {deadlineDate != null ? formatDayJs(deadlineDate) : "–"}
				</Typography>
				<Typography variant={"body2"} color={"text.secondary"}>
					{earliestStartDate != null && `${i18n.t("earliest_start_date")}: ${formatDayJs(earliestStartDate)}`}
				</Typography>
			</Box>
			<AsyncSelectField<AppUser, number>
				label={i18n.t("executor")}
				fetchOptions={({ searchQuery, currentSelection }) =>
					UserApi.getUserSelectionOptions({
						searchQuery: searchQuery,
						currentSelectionUserId: currentSelection,
					})
				}
				defaultValue={assignedToUserId}
				getOptionKey={(option) => option.id}
				getOptionLabel={(option) => option.name}
				onChange={(user) => onUserChange(user?.id ?? null)}
			/>
		</Paper>
	);
};
